import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Calendar, FileText, Users, Download, Eye, Trash2, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { operationsBase, TABLES } from '../../../lib/airtable.js';

/**
 * External Survey Import History Component
 * Shows all past survey imports with details and actions
 */
export function ExternalSurveyHistory() {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImport, setSelectedImport] = useState(null);
  const [deletingImport, setDeletingImport] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadImportHistory();
  }, []);

  useEffect(() => {
    // Listen for delete events from the details view
    const handleDeleteFromDetails = (event) => {
      handleDeleteClick(event.detail);
    };

    window.addEventListener('deleteImport', handleDeleteFromDetails);
    return () => {
      window.removeEventListener('deleteImport', handleDeleteFromDetails);
    };
  }, []);

  const loadImportHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all external survey imports, ordered by most recent first
      // Only request fields that actually exist in the table
      const records = await operationsBase(TABLES.EXTERNAL_SURVEY_IMPORTS)
        .select({
          sort: [{ field: 'Import Date', direction: 'desc' }],
          fields: [
            'Client',
            'Program', 
            'Import Date',
            'Source Type',
            'Survey Title',
            'Original Filename',
            'Response Count',
            'Question Mapping',
            'Import Notes',
            'Imported By'
          ]
        })
        .all();

      // For each record, we need to fetch the linked record names
      const processedImports = await Promise.all(records.map(async (record) => {
        let clientName = 'Unknown';
        let programName = 'Unknown';

        // Get client name from linked record
        const clientIds = record.get('Client');
        if (clientIds && clientIds.length > 0) {
          try {
            const clientRecord = await operationsBase('Clients').find(clientIds[0]);
            clientName = clientRecord.get('Client Long Name') || clientRecord.get('ClientID') || 'Unknown';
          } catch (err) {
            console.warn('Failed to fetch client name:', err);
          }
        }

        // Get program name from linked record
        const programIds = record.get('Program');
        if (programIds && programIds.length > 0) {
          try {
            const programRecord = await operationsBase('Programs').find(programIds[0]);
            programName = programRecord.get('Full Program Name') || programRecord.get('ProgramID') || 'Unknown';
          } catch (err) {
            console.warn('Failed to fetch program name:', err);
          }
        }

        return {
          id: record.id,
          client: clientName,
          program: programName,
          associatedSOs: '', // Field doesn't exist yet in table, will be empty
          importDate: record.get('Import Date'),
          sourceType: record.get('Source Type'),
          surveyTitle: record.get('Survey Title'),
          originalFilename: record.get('Original Filename'),
          responseCount: record.get('Response Count') || 0,
          questionMapping: record.get('Question Mapping') ? JSON.parse(record.get('Question Mapping')) : null,
          importNotes: record.get('Import Notes'),
          importedBy: record.get('Imported By'),
          responseRecords: [], // Will be loaded separately when viewing details
          createdTime: record.createdTime
        };
      }));

      setImports(processedImports);
    } catch (err) {
      console.error('Failed to load import history:', err);
      setError('Failed to load import history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const handleViewDetails = (importRecord) => {
    setSelectedImport(importRecord);
  };

  const handleCloseDetails = () => {
    setSelectedImport(null);
  };

  const handleDeleteClick = (importRecord) => {
    setDeletingImport(importRecord);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingImport) return;
    
    try {
      // First delete all response records associated with this import
      const responseRecords = deletingImport.responseRecords || [];
      if (responseRecords.length > 0) {
        // Delete responses in batches of 10 (Airtable limit)
        const batchSize = 10;
        for (let i = 0; i < responseRecords.length; i += batchSize) {
          const batch = responseRecords.slice(i, i + batchSize);
          await operationsBase(TABLES.EXTERNAL_SURVEY_RESPONSES).destroy(batch);
        }
      }

      // Then delete the import record itself
      await operationsBase(TABLES.EXTERNAL_SURVEY_IMPORTS).destroy(deletingImport.id);

      // Update the local state to remove the deleted import
      setImports(prev => prev.filter(imp => imp.id !== deletingImport.id));
      
      // Close any open details view if it was the deleted import
      if (selectedImport && selectedImport.id === deletingImport.id) {
        setSelectedImport(null);
      }

      setShowDeleteConfirm(false);
      setDeletingImport(null);
      
    } catch (error) {
      console.error('Failed to delete import:', error);
      setError(`Failed to delete import: ${error.message}`);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeletingImport(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading import history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
          <Button
            onClick={loadImportHistory}
            className="mt-2"
            variant="outline"
            size="sm"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (selectedImport) {
    return <ImportDetails importRecord={selectedImport} onClose={handleCloseDetails} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Survey Import History</h2>
          <p className="text-gray-600 mt-1">View and manage all external survey imports</p>
        </div>
        <Button onClick={loadImportHistory} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {imports.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Imports Yet</h3>
            <p className="text-gray-600">
              Upload your first external survey to see it appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {imports.map((importRecord) => (
            <Card key={importRecord.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {importRecord.surveyTitle}
                      </h3>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-800">
                          {importRecord.client}
                        </Badge>
                        <Badge className="bg-green-100 text-green-800">
                          {importRecord.program}
                        </Badge>
                        {importRecord.associatedSOs && (
                          <Badge className="bg-purple-100 text-purple-800">
                            SO {importRecord.associatedSOs}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(importRecord.importDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>{importRecord.responseCount} responses</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span className="truncate">{importRecord.originalFilename}</span>
                      </div>
                    </div>

                    <div className="mt-3 text-sm">
                      <span className="text-gray-500">Questions: </span>
                      <span className="text-gray-700">
                        {importRecord.questionMapping?.questions?.length || 0} detected
                      </span>
                      {importRecord.questionMapping?.questions && (
                        <span className="text-gray-500 ml-2">
                          ({importRecord.questionMapping.questions.slice(0, 3).map(q => q.title.substring(0, 30)).join(', ')}
                          {importRecord.questionMapping.questions.length > 3 ? '...' : ''})
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      onClick={() => handleViewDetails(importRecord)}
                      variant="outline"
                      size="sm"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      onClick={() => handleDeleteClick(importRecord)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && deletingImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Survey Import</h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete this survey import?
              </p>
              <div className="bg-gray-50 p-3 rounded border">
                <p className="font-medium text-gray-900">{deletingImport.surveyTitle}</p>
                <p className="text-sm text-gray-600">
                  {deletingImport.responseCount} responses • {formatDate(deletingImport.importDate)}
                </p>
              </div>
              <p className="text-sm text-red-600 mt-2">
                This will permanently delete the import record and all {deletingImport.responseCount} associated response records.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                onClick={handleDeleteCancel}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
                size="sm"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Import
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Import Details Component
 * Shows detailed view of a specific import
 */
function ImportDetails({ importRecord, onClose }) {
  const [responses, setResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(true);
  
  // Table enhancement states
  const [filteredResponses, setFilteredResponses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('responseNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    loadResponses();
  }, [importRecord.id]);

  const loadResponses = async () => {
    try {
      setLoadingResponses(true);
      
      // Fetch response records for this import using client-side filtering (most reliable)
      const allRecords = await operationsBase(TABLES.EXTERNAL_SURVEY_RESPONSES)
        .select({
          fields: ['Response ID', 'Response Data', 'Response Date', 'Response Number', 'Survey Import'],
          sort: [{ field: 'Response Number', direction: 'asc' }]
        })
        .all();
      
      // Filter client-side for reliable matching
      const records = allRecords.filter(record => {
        const surveyImportField = record.get('Survey Import');
        if (Array.isArray(surveyImportField)) {
          return surveyImportField.includes(importRecord.id);
        }
        return surveyImportField === importRecord.id;
      });

      const processedResponses = records.map(record => {
        const rawResponseData = record.get('Response Data');
        let responseData = null;
        
        try {
          // Handle different data formats
          if (rawResponseData) {
            if (typeof rawResponseData === 'string') {
              responseData = JSON.parse(rawResponseData);
            } else {
              responseData = rawResponseData;
            }
          }
        } catch (error) {
          console.warn('Failed to parse response data:', error, rawResponseData);
          responseData = null;
        }

        return {
          id: record.id,
          responseId: record.get('Response ID'),
          responseData: responseData,
          responseDate: record.get('Response Date'),
          responseNumber: record.get('Response Number')
        };
      });

      console.log(`Processed ${processedResponses.length} responses`);
      if (processedResponses.length > 0) {
        console.log('Sample processed response:', {
          id: processedResponses[0].id,
          responseId: processedResponses[0].responseId,
          responseNumber: processedResponses[0].responseNumber,
          hasResponseData: !!processedResponses[0].responseData,
          responseDataKeys: processedResponses[0].responseData ? Object.keys(processedResponses[0].responseData) : []
        });
      }

      setResponses(processedResponses);
    } catch (err) {
      console.error('Failed to load responses:', err);
    } finally {
      setLoadingResponses(false);
    }
  };

  // Filter and sort responses when data changes
  useEffect(() => {
    let filtered = [...responses];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(response => {
        const responseData = response.responseData?.responses || {};
        const searchText = searchTerm.toLowerCase();
        
        // Search in response number
        if (String(response.responseNumber || '').toLowerCase().includes(searchText)) {
          return true;
        }
        
        // Search in answer values
        return Object.values(responseData).some(answer => {
          if (typeof answer === 'object' && answer?.answer) {
            return String(answer.answer).toLowerCase().includes(searchText);
          }
          return String(answer).toLowerCase().includes(searchText);
        });
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      if (sortField === 'responseNumber') {
        aValue = a.responseNumber || 0;
        bValue = b.responseNumber || 0;
      } else if (sortField.startsWith('question_')) {
        const questionId = sortField.replace('question_', '');
        const aData = a.responseData?.responses || {};
        const bData = b.responseData?.responses || {};
        
        aValue = aData[questionId]?.answer || aData[questionId] || '';
        bValue = bData[questionId]?.answer || bData[questionId] || '';
      } else {
        aValue = a[sortField] || '';
        bValue = b[sortField] || '';
      }
      
      // Convert to strings for consistent comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    setFilteredResponses(filtered);
  }, [responses, searchTerm, sortField, sortDirection]);

  // Sorting handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper function to extract answer value safely
  const extractAnswer = (answer) => {
    if (!answer) return '';
    
    // Handle corrupted "[object Object]" strings
    if (answer === "[object Object]") {
      return "(corrupted data)";
    }
    
    // Handle objects with answer property
    if (typeof answer === 'object' && answer !== null) {
      if (answer.answer !== undefined) {
        return String(answer.answer);
      }
      // If it's an object but no answer property, try to extract meaningful value
      if (Object.keys(answer).length === 1) {
        return String(Object.values(answer)[0]);
      }
      // Last resort - stringify the object
      try {
        return JSON.stringify(answer);
      } catch {
        return "(complex data)";
      }
    }
    
    return String(answer);
  };

  // Modal handlers
  const openModal = (response) => {
    setSelectedResponse(response);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedResponse(null);
    setShowModal(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button onClick={onClose} variant="outline" size="sm" className="mb-4">
            ← Back to History
          </Button>
          <h2 className="text-2xl font-bold text-gray-900">{importRecord.surveyTitle}</h2>
          <p className="text-gray-600 mt-1">Import Details</p>
        </div>
        <Button
          onClick={() => {
            // Go back to main view and trigger delete
            onClose();
            // Small delay to ensure state transition, then trigger delete
            setTimeout(() => {
              // We need to pass the delete trigger up to parent
              window.dispatchEvent(new CustomEvent('deleteImport', { detail: importRecord }));
            }, 100);
          }}
          variant="outline"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4 mr-1" />
          Delete Import
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Import Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <span className="font-medium text-gray-700">Client:</span>
              <span className="ml-2 text-gray-600">{importRecord.client}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Program:</span>
              <span className="ml-2 text-gray-600">{importRecord.program}</span>
            </div>
            {importRecord.associatedSOs && (
              <div>
                <span className="font-medium text-gray-700">Associated SOs:</span>
                <span className="ml-2 text-gray-600">{importRecord.associatedSOs}</span>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-700">Source:</span>
              <span className="ml-2 text-gray-600">{importRecord.sourceType}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Original File:</span>
              <span className="ml-2 text-gray-600">{importRecord.originalFilename}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Import Date:</span>
              <span className="ml-2 text-gray-600">{formatDate(importRecord.importDate)}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Imported By:</span>
              <span className="ml-2 text-gray-600">{importRecord.importedBy}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Responses:</span>
              <span className="ml-2 text-gray-600">{importRecord.responseCount}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Questions ({importRecord.questionMapping?.questions?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {importRecord.questionMapping?.questions?.map((question, index) => (
                <div key={question.id} className="text-sm">
                  <span className="font-medium text-gray-700">Q{index + 1}:</span>
                  <span className="ml-2 text-gray-600">{question.title.substring(0, 100)}</span>
                  {question.title.length > 100 && <span className="text-gray-400">...</span>}
                </div>
              )) || <p className="text-gray-500">No questions found</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Survey Overview - Typeform Style */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Survey Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingResponses ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading responses...</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{responses.length}</div>
                  <div className="text-sm text-gray-600">Total Responses</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{importRecord.questionMapping?.questions?.length || 0}</div>
                  <div className="text-sm text-gray-600">Questions</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {responses.length > 0 ? Math.round((responses.filter(r => r.responseData && r.responseData.responses && Object.keys(r.responseData.responses).length > 0).length / responses.length) * 100) : 0}%
                  </div>
                  <div className="text-sm text-gray-600">Completion Rate</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {formatDate(importRecord.importDate)}
                  </div>
                  <div className="text-sm text-gray-600">Imported On</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Response Data Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Response Data ({filteredResponses.length} of {responses.length} responses)</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search responses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingResponses ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading responses...</span>
              </div>
            ) : filteredResponses.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>{searchTerm ? 'No responses match your search.' : 'No responses found for this import.'}</p>
              </div>
            ) : (
              <div className="border rounded-lg">
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-sm min-w-full">
                    <thead className="sticky top-0 bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium min-w-[100px] sticky left-0 bg-gray-50 border-r shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] z-20 relative">
                          <button
                            onClick={() => handleSort('responseNumber')}
                            className="flex items-center gap-1 hover:text-blue-600"
                          >
                            Response #
                            {sortField === 'responseNumber' && (
                              sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        </th>
                        {importRecord.questionMapping?.questions?.map((question, index) => (
                          <th key={question.id} className="text-left p-3 font-medium min-w-[200px]">
                            <button
                              onClick={() => handleSort(`question_${question.id}`)}
                              className="flex items-center gap-1 hover:text-blue-600 text-left"
                            >
                              <div className="min-w-[200px] max-w-[300px] group relative">
                                <div className="font-medium">Q{index + 1}</div>
                                <div className="text-xs text-gray-500 font-normal whitespace-normal break-words leading-tight">
                                  {question.title}
                                </div>
                                
                                {/* Hover tooltip for full question if long */}
                                {question.title.length > 50 && (
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 max-w-md whitespace-normal break-words">
                                    <div className="font-medium">Q{index + 1}</div>
                                    <div className="mt-1">{question.title}</div>
                                    {/* Arrow pointing down */}
                                    <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                )}
                              </div>
                              {sortField === `question_${question.id}` && (
                                sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          </th>
                        )) || null}
                        <th className="text-left p-3 font-medium min-w-[100px] sticky right-0 bg-gray-50 border-l shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredResponses.map((response, responseIndex) => {
                      const responseData = response.responseData || {};
                      // The actual responses are in responseData.responses, not at the root level
                      const actualResponses = responseData.responses || {};
                      
                      
                      return (
                        <tr key={response.id} className="hover:bg-gray-50">
                          <td className="p-3 font-medium sticky left-0 bg-white border-r shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] z-10 relative">
                            #{response.responseNumber || responseIndex + 1}
                          </td>
                          {importRecord.questionMapping?.questions?.map((question) => {
                            // Extract answer using our helper function
                            const rawAnswer = actualResponses[question.id];
                            const answer = extractAnswer(rawAnswer);
                            const displayAnswer = answer || '(empty)';
                            
                            return (
                              <td key={question.id} className="p-3 min-w-[200px]">
                                <div className="max-w-[180px] group relative">
                                  <div className="truncate cursor-help">
                                    {displayAnswer}
                                  </div>
                                  
                                  {/* Hover tooltip for long text */}
                                  {displayAnswer.length > 20 && (
                                    <div className="absolute bottom-full left-0 mb-2 px-4 py-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 min-w-[300px] max-w-[500px] whitespace-pre-wrap break-words">
                                      <div className="max-h-48 overflow-y-auto">
                                        {displayAnswer}
                                      </div>
                                      {/* Arrow pointing down */}
                                      <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          }) || null}
                          <td className="p-3 sticky right-0 bg-white border-l shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openModal(response)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View Full
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Response Detail Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Response #{selectedResponse?.responseNumber || 'N/A'} - Complete Details
              </DialogTitle>
            </DialogHeader>
            {selectedResponse && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-700">Response Number:</span>
                    <span className="ml-2 text-gray-600">#{selectedResponse.responseNumber}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Response ID:</span>
                    <span className="ml-2 text-gray-600 text-sm">{selectedResponse.id}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium">All Responses</h3>
                  {importRecord.questionMapping?.questions?.map((question, index) => {
                    const actualResponses = selectedResponse.responseData?.responses || {};
                    const rawAnswer = actualResponses[question.id];
                    const answer = extractAnswer(rawAnswer);

                    return (
                      <div key={question.id} className="border rounded-lg p-4">
                        <div className="font-medium text-gray-900 mb-2">
                          Q{index + 1}: {question.title}
                        </div>
                        <div className="text-gray-700 bg-white p-3 rounded border min-h-[3rem] whitespace-pre-wrap">
                          {answer || '(no response)'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={closeModal}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}